/**
 * DOB (Date of Birth) picker controller
 *
 * UX only: prefills year/month/day from initial value, clears invalid day and
 * adds red border when day is out of range for the month or in the future.
 * The form submits dob_year, dob_month, dob_day; the backend reconciles into dob.
 */
import { Controller } from "@hotwired/stimulus"

const DAY_ERROR_CLASSES = ["border-red-500", "ring-red-500"]

export default class extends Controller {
  static targets = ["year", "month", "day"]
  static values = { minAge: { type: Number, default: 18 }, initial: String }

  connect() {
    this.prefill()
    this.update()
  }

  prefill() {
    const raw = this.initialValue
    if (!raw || typeof raw !== "string") return
    const parts = raw.trim().split("-")
    if (parts.length !== 3) return
    const [y, m, d] = parts.map((p) => parseInt(p, 10))
    if (!this.hasYearTarget || !this.monthTarget || !this.dayTarget) return
    if (Number.isInteger(y) && y > 0) this.yearTarget.value = String(y)
    if (Number.isInteger(m) && m >= 1 && m <= 12) this.monthTarget.value = String(m)
    if (Number.isInteger(d) && d >= 1 && d <= 31) this.dayTarget.value = String(d)
    this.update()
  }

  update() {
    this.cleanupDayIfNeeded()
  }

  cleanupDayIfNeeded() {
    if (!this.hasDayTarget) return
    const year = this.getYear()
    const month = this.getMonth()
    const day = this.getDay()
    if (!year || !month || !day) {
      this.setDayValid()
      return
    }
    const maxDay = this.daysInMonth(year, month)
    const isDayOutOfRange = day < 1 || day > maxDay
    const isFuture = this.isFutureDate(year, month, day)
    if (isDayOutOfRange || isFuture) {
      this.dayTarget.value = ""
      this.setDayInvalid()
    } else {
      this.setDayValid()
    }
  }

  daysInMonth(year, month) {
    if (month === 2) return this.isLeapYear(year) ? 29 : 28
    if ([4, 6, 9, 11].includes(month)) return 30
    return 31
  }

  isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  }

  isFutureDate(year, month, day) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const d = new Date(year, month - 1, day)
    d.setHours(0, 0, 0, 0)
    return d.getTime() > today.getTime()
  }

  setDayInvalid() {
    if (!this.hasDayTarget) return
    DAY_ERROR_CLASSES.forEach((c) => this.dayTarget.classList.add(c))
  }

  setDayValid() {
    if (!this.hasDayTarget) return
    DAY_ERROR_CLASSES.forEach((c) => this.dayTarget.classList.remove(c))
  }

  getYear() {
    return this.hasYearTarget ? parseInt(this.yearTarget.value, 10) : null
  }

  getMonth() {
    return this.hasMonthTarget ? parseInt(this.monthTarget.value, 10) : null
  }

  getDay() {
    return this.hasDayTarget ? parseInt(this.dayTarget.value, 10) : null
  }
}
